const getBaseConfig = require('../../common/getBaseConfig');
const { arrify } = require('../../utilities/array');
const { createPromisesQueue, queueInputTypes } = require('../../utilities/queue');

const mitt = require('mitt').default;

const BASE_CONFIG = getBaseConfig();

/**
 * Pub/Sub events for triggering commands
 */
const COMMANDS = {
    /**
     * Push the analytics event related to the first step of the checkout
     * @param {Object} payload
     * @param {string} payload.currencyCode Currency code
     * @param {string} payload.eventDataLabel The label of the analytics event
     * @param {object[]} payload.productFieldObjects Array of DTOs of the
     * productFieldObjects from the cart items
     */
    sendEventFirstStepOfCheckout: 'datalayer__command-send-event-checkout-step-1',
    /**
     * Push the analytics event related to the first step of the checkout
     * @param {Object} datalayerPurchaseInfo Data from the server
     */
    sendEventPurchase: 'datalayer__command-send-event-purchase'
};

/**
 * Pub/Sub events for target actions to collect data from
 */
const EVENTS = {
    /**
     * Event triggered after submitting the abandoned cart modal form,
     * once a valid contact has been resolved in SFMC (either newly
     * created or retrieved if it already existed).
     *
     * This event guarantees that a contactKey is available and
     * associated with the provided email.
     *
     * @param {Object} payload The payload of the event
     * @param {string} payload.contactKey The unique identifier of the related
     *   contact
     * @param {string} payload.email The email address provided by the guest
     *   user
     */
    abandonedCartModalContactReady: 'analytics__abandoned-cart-contact-ready',
    /**
     * Event triggered when a user views any abandoned cart modal.
     *
     * @param {Object} payload The payload of the event
     * @param {string} payload.modalType The type of the modal (consent or save
     * my basket for guest users)
     */
    abandonedCartModalImpression: 'analytics__abandoned-cart-modal-impression',
    /**
     * Event triggered when a user interacts with any abandoned cart modal.
     *
     * @param {Object} payload The payload of the event
     * @param {string} payload.interaction The type of interaction
     * @param {string} payload.modalType The type of the modal (consent or save
     * my basket for guest users)
     */
    abandonedCartModalInteracted: 'analytics__abandoned-cart-modal-interacted',
    /**
     * Event triggered after sending and event to datalayer with
     * sendEvent helper
     * @param {DataLayerEvent} datalayerEvent - event sent
     */
    datalayerEventSent: 'datalayer__event-sent',
    /**
     * Event triggered when an error occurs during the checkout process
     * @param {object} payload - The data
     * @param {string} payload.stageName - The name of the stage when the error
     * has occurred
     */
    checkoutError: 'datalayer__checkout-error',
    /**
     * Event triggered after changing the stage in the checkout process
     * @param {object} payload - The data
     * @param {string} payload.stageName - The name of the stage
     * @param {string} payload.redirectUrl - URL to redirect after pushing the
     * event
     */
    checkoutStageChanged: 'datalayer__checkout-stage-changed',
    /**
     * Event triggered after the first into-view appearence of a product
     * impression
     * @param {Object} payload - Payload
     * @param {Element} payload.element - The element which triggers the
     * impression
     * @param {ImpressionFieldObject} payload.impression - Impression data
     */
    impressionShownForTheFirstTime: 'datalayer__impression-first-time-shown',
    /**
     * Event triggered after sending some impressions to datalayer
     * @param {ImpressionFieldObject[]} payload - Array of impressions sent to
     * datalayer
     */
    impressionsSent: 'datalayer__impressions-sent',
    /**
     * Event triggered after creating and showing the modal (full content loaded)
     * @param {object} payload
     * @param {Element} payload.modal - modal element
     * @param {string} payload.name - Name of the modal
     * @param {Element} payload.scrollableContainer - the element that has the
     * scroll
     */
    modalShown: 'analytics__modal-shown',
    /**
     * Event triggered when closing the modal, before modal content removing
     * @param {object} payload
     * @param {Element} payload.modal - modal element
     * @param {string} payload.name - Name of the modal
     * @param {Element} payload.scrollableContainer - the element that has the
     * scroll
     */
    modalWillBeHidden: 'analytics__modal-will-be-hidden',
    /**
     * Event triggered after closing a modal
     * @param {object} payload
     * @param {string} payload.name - Name of the modal
     */
    modalHidden: 'analytics__modal-hidden',
    /**
     * Event triggered when loading a page.
     * For instance: It can be used for sending the Google Tag Manager page view
     * event.
     * @param {Object} payload The event payload
     * @param {string} [payload.categoryName] The name of the category of PLP
     * @param {string} [payload.pid] The ID of the main product of PDP
     * @param {string} [payload.searchKeywords] The search term used in
     * Search-Show
     * @param {Object} payload.referral Referral info added by middleware function
     * @param {string} payload.referral.referrerUrl Referrer url
     * @param {string} payload.referral.source Source
     */
    pageviewLoaded: 'analytics__pageview',
    /**
     * Event triggered after adding a product to cart
     * @property {object} cart - Cart info
     * @property {ProductFieldObject.ProductFieldObject[]} cart.items - The
     * product field objects of all the items in cart
     * @param {string} currencyCode - The current currency code
     * of the clicked product returned from server
     * @param {Object} productFieldObject - The DTO of the productFieldObject
     * of the just added product returned from server
     * @param {Element} target - The element that has a the product impression
     * or a descendant
     */
    productAddedToCart: 'datalayer__product-added-to-cart',
    /**
     * Event triggered after clicking a product
     * @param {string} currencyCode - The current currency code
     * of the clicked product returned from server
     * @param {Object} productFieldObject - The DTO of the productFieldObject
     * of the clicked product returned from server
     * @param {string} redirectUrl - The Url to redirect after pushing
     * the data to datalayers
     * @param {Element} target - The element just clicked
     */
    productClicked: 'datalayer__product-clicked',
    /**
     * Event triggered after clicking a product color in the color selector of
     * PDP
     * @param {string} currencyCode The current currency code of the clicked
     * product returned from server
     * @param {string} previousProductId The product ID of the previous color
     * @param {Object} productFieldObject The DTO of the productFieldObject
     * of the clicked color returned from server
     */
    productColorClicked: 'datalayer__product-color-clicked',
    /**
     * Event triggered after removed a product from cart
     * @property {object} cart - Cart info
     * @property {ProductFieldObject.ProductFieldObject[]} cart.items - The
     * product field objects of all the items in cart
     * @param {string} currencyCode - The current currency code
     * of the clicked product returned from server
     * @param {Object} productFieldObject - The DTO of the productFieldObject
     * of the just removed product returned from server
     */
    productRemovedFromCart: 'datalayer__product-removed-from-cart',
    /**
     * Event triggered after finishing an order
     * @param {Object} payload Event useful info
     * @param {string} payload.allCouponsApplied The coupons applied to each
     * item of the order
     * @param {string} payload.countryCode The country code of the purchase
     * @param {string} payload.currencyCode The current currency code
     * @param {ProductFieldObject.ProductFieldObject[]} payload.items Product
     * field objects of all the items in cart
     * @param {string} payload.grandTotal The grand total price gross of tax
     * for LineItemCtnr, in purchase currency. Total prices represent the sum of
     * product prices, services prices and adjustments. (Decimal price)
     * @param {string} payload.orderNumber The number of the new order
     * @param {string} payload.totalShippingCost The shipping total price. If
     * the line item container is based on net pricing the shipping total net
     * price is returned. If the line item container is based on gross pricing
     * the shipping total gross price is returned. (Decimal price)
     * @param {string} payload.totalTax The grand total tax for LineItemCtnr,
     * in purchase currency. Total prices represent the sum of product prices,
     * services prices and adjustments. (Decimal price)
     */
    purchased: 'analytics__purchased',
    /**
     * Event triggered after setting the content segment
     * @param {number} segment - segment new value
     */
    segmentSet: 'analytics__segment-set',
    /**
     * Event triggered after creating and showing shop the look modal
     * @param {object} payload
     * @param {Element} payload.modal - shop-the-look modal
     * @param {Element} payload.scrollableContainer - the element that has the
     * scroll
     */
    shopTheLookModalShown: 'analytics__shop-the-look-modal-shown',
    /**
     * Event triggered when hiding the modal is triggered
     * removing
     * @param {object} payload
     * @param {Element} payload.modal - shop-the-look modal
     * @param {Element} payload.scrollableContainer - the element that has the
     * scroll
     */
    shopTheLookModalWillBeHidden: 'analytics__shop-the-look-modal-will-be-hidden',
    /**
     * Event triggered after hiding shop the look
     */
    shopTheLookModalHidden: 'analytics__shop-the-look-modal-hidden',
    /**
     * Event triggered after changing the active slide in a swiper slider of
     * products
     * @param {Swiper} swiper
     */
    sliderActiveSlideChanged: 'analytics__slider-active-slide-changed',
    /**
     * Event triggered after user interaction stops due to idle state
     * @param {Object} payload
     * @param {number} [payload.millisecondsSinceLastAction] The milliseconds
     *  since last action. This is useful because the event can be configured
     *  to be used for diferent idle times. There is no a common config. For
     *  instance: Abandoned Cart modal publishes it according a custom site
     *  preference named `adAcIdleTime`.
     * @param {string} payload.source Optional identifier of the
     *  feature/observer that emitted the event (e.g., "abandonedCart",
     *  "sessionTimeout", "globalIdle"). Helps consumers decide whether to act.
     */
    userInteractionStopped: 'analytics__user-interaction-stopped',
    /**
     * Event triggered after user attempts to exit: mouse leave,
     *  blur or visibility change
     */
    userExitIntentAttempted: 'analytics__user-exit-intent-attempted',
    /**
     * Event triggered when the user has successfully logged in.
     * IMPORTANT: This event is launch before a redirection, so the subscribers
     * must be always synchronous
     *
     * @param {Object} payload The payload of the event
     * @param {Object} payload.authenticatedCustomer The customer info object
     * @param {string} payload.authenticatedCustomer.ID The ID of the customer
     * @param {string} payload.authenticatedCustomer.email The email of the
     *  just logged customer
     */
    userLoggedIn: 'analytics__user-logged-in'
};

/**
 * Constant values of payloads of some events.
 *
 * Defined by means of a IIFE because some consts can be shared between events
 * and we need a private `shared` property in order to avoid duplicating consts.
 *
 * Object structure:
 *   {
 *     [event1-name]: {
 *       [param]: {
 *          [value1]: any,
 *          [value2]: any,
 *          ...
 *          [valueN]: any,
 *       },
 *       [param2]: shared.paramXY
 *     },
 *     [event2-name]: {
 *       [param]: shared.paramXY
 *     }
 *   }
 */
const PAYLOADS = (function () {
    const shared = {
        modalName: {
            addedToCart: 'added-to-cart',
            minicart: 'minicart',
            shopTheLook: 'shop-the-look',
            searchSuggestions: 'search-suggestions'
        },
        abandonedCartModalType: {
            /** @type {string} Modal shown to logged-in customers to request consent to track their cart. */
            consent: BASE_CONFIG.abandonedCartConfig.analytics.modalType.consent,
            /** @type {string} Modal shown to guest users who appear undecided, offering cart-tracking help. */
            guest: BASE_CONFIG.abandonedCartConfig.analytics.modalType.guest
        }
    };

    return {
        abandonedCartModalImpression: {
            modalType: shared.abandonedCartModalType
        },
        abandonedCartModalInteracted: {
            interaction: {
                /** @type {string} user closes or cancels modal negatively, without reading or submitting the form */
                closeOrCancel: 'closeOrCancel',
                /** @type {string} user closed modal with the aim of ending the purchase */
                continueShopping: 'continue',
                /** @type {string} form successfully submitted and modal closed */
                submitEmailSuccess: 'emailProvided'
            },
            modalType: shared.abandonedCartModalType
        },
        modalHidden: {
            name: shared.modalName
        },
        modalShown: {
            name: shared.modalName
        },
        modalWillBeHidden: {
            name: shared.modalName
        },
        userInteractionStopped: {
            source: {
                abandonedCartModal: 'abandonedCartModal'
            }
        }
    };
}());

// Assure a single broker for every js which requires this module.
const broker = window.adAnalyticsBroker || mitt();
window.adAnalyticsBroker = broker;

const middlewares = (function () {
    const queues = {};
    const existsQueue = (eventName) => eventName in queues;
    return {
        push(eventName, middleware) {
            if (!existsQueue(eventName)) {
                queues[eventName] = createPromisesQueue({
                    inputType: queueInputTypes.SEQUENTIAL_INPUT
                });
            }
            queues[eventName].add(middleware);
        },
        processPayload(eventName, payload) {
            return queues[eventName]?.start(payload) ?? Promise.resolve(payload);
        }
    };
}());

const syncBroker = (function () {
    const queues = {};
    const existsQueue = (eventName) => eventName in queues;
    return {
        on(eventName, handler) {
            if (!existsQueue(eventName)) {
                queues[eventName] = createPromisesQueue({
                    inputType: queueInputTypes.COMMON_INPUT
                });
            }
            queues[eventName].add(handler);
        },
        off(eventName, handler) {
            queues[eventName]?.remove(handler);
        },
        emit(eventName, payload) {
            return queues[eventName]?.start(payload) ?? Promise.resolve();
        }
    };
}());

const eventNames = Object.values(EVENTS);
const commandNames = Object.values(COMMANDS);
const validNames = [...eventNames, ...commandNames];
const existsEvent = (eventName) => validNames.includes(eventName);
const assertEventExists = (eventName) => {
    if (!existsEvent(eventName)) {
        throw new TypeError(`Unknown analytic event: '${eventName}'`);
    }
};

/**
 * Creates a function that applies a given action to every combination
 * of events and handlers/middlewares/callbacks.
 * The resulting function accepts single values or arrays for both events and
 * handlers.
 *
 * @example
 *   const subscribe = createMultiEventHandlerFunction((event, fn, sync) => {
 *       broker.on(event, fn, sync);
 *   });
 *
 *   // Single event and single handler
 *   subscribe('userLogin', handleLogin);
 *
 *   // Multiple events and multiple handlers
 *   subscribe(['login', 'logout'], [handleLogin, handleLogout]);
 *
 *   // Passing additional arguments to the action
 *   subscribe('criticalEvent', handleCritical, true);
 *
 * @param {function(string, function): void} action
 *  Function to apply to each (event, handler, ...args) pair
 * @returns {function((string|string[]), (function|function[]), ...*): void}
 *  A function that accepts events, handlers, and additional arguments
 */
const createMultiEventHandlerFunction = (action) => (events, handlers, ...args) => {
    const eventsArray = arrify(events);
    const handlersArray = arrify(handlers);

    eventsArray.forEach((event) => {
        handlersArray.forEach((handler) => {
            action(event, handler, ...args);
        });
    });
};

/**
 * Preprocess the payload of an analytic event before it reaches to the
 * subscriber functions
 * @param {string|string[]} eventName The event/s
 * @param {function((Event|JQuery.event|any), any)|function[]} middleware The middleware/s of the
 * subscription, which receives an event
 */
const appendMiddleware = createMultiEventHandlerFunction(
    (eventName, middleware) => {
        assertEventExists(eventName);
        middlewares.push(eventName, middleware);
    }
);

/**
 * Publish an analytic event
 * @param {ANALYTICS_EVENTS} eventName The event to publish to
 * @param {*} [data] The payload that is useful for event subscribers
 */
async function publish(eventName, data) {
    assertEventExists(eventName);
    const processedData = await middlewares.processPayload(eventName, data);
    await syncBroker.emit(eventName, processedData);
    broker.emit(eventName, processedData);
}

/**
 * Subscribe to an analytic event
 * @param {string|string[]} eventName The event/s to subscribe to
 * @param {function((Event|JQuery.event|any), any)} handler The handler of the
 * subscription, which receives an event
 * @param {boolean} [synchronous=false] The handler will be called synchronously
 * and before the async handlers
 */
const subscribe = createMultiEventHandlerFunction(
    (eventName, handler, synchronous = false) => {
        assertEventExists(eventName);
        if (synchronous === true) {
            syncBroker.on(eventName, handler);
        } else {
            broker.on(eventName, handler);
        }
    }
);

/**
 * Unsubscribe from an analytic event
 * @param {string|string[]} eventName The event/s from ANALYTICS_EVENTS to
 * unsubscribe from
 * @param {function((Event|JQuery.event|any), any)} handler The handler of the
 * subscription that must be deleted
 */
const unsubscribe = createMultiEventHandlerFunction(
    (eventName, handler) => {
        assertEventExists(eventName);
        syncBroker.off(eventName, handler);
        broker.off(eventName, handler);
    }
);

/* eslint-disable no-console */
// Disable no-console because the following code is only for debugging purpose
const debug = {
    mustShowMoreInfo: false,
    print: (eventName) => (payload) => {
        console.group(`[AD/analytics] "${eventName}"`);
        console.log('payload:', payload);
        if (debug.mustShowMoreInfo) {
            console.trace();
        }
        console.groupEnd();
    },
    on(mustShowMoreInfo) {
        this.mustShowMoreInfo = mustShowMoreInfo ?? false;
        const callback = this.print;
        broker.on('*', (e, data) => callback(e)(data));
        console.log('[AD/analytics] DEBUG ON');
    }
};
/* eslint-enable no-console */

const internalEventCenter = {
    COMMANDS,
    EVENTS,
    PAYLOADS,
    debug,
    appendMiddleware,
    publish,
    subscribe,
    unsubscribe
};

module.exports = {
    internalEventCenter
};
